import styled from '@emotion/styled';
import { useState } from 'react';
import { Bars } from 'react-loader-spinner';
import SkillsScreen from '../mainTabs/profile/SkillsScreen';
import TitlesScreen from '../mainTabs/profile/TitlesScreen';
import JobInfoModal from '../profile/modals/JobInfoModal';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { clearAuthError, setOnboarding } from '../redux/reducers/userReducer';
import { AppColors } from '../theme/AppTheme';
import Enable2FAScreen from './Enable2FAScreen';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import LoginScreen from './LoginScreen';
import OnboardingResumeScreen from './OnboardingResumeScreen';
import SignUpInfoScreen from './SignUpInfoScreen';
import SignUpScreen from './SignUpScreen';
import VerifyEmailScreen from './VerifyEmailScreen';
import UserTypeManager from '../shared/utils/UserCodeManager';

const StyledLoadingDiv = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
}));

export enum AuthType {
    signUp,
    signUpInfo,
    googleInfo,
    selectSkills,
    selectPositions,
    jobInfo,
    verifyEmail,
    login,
    forgot,
    replaceResume,
    uploadResume,
    none
}

export interface OnboardingOptions {
    open: boolean,
    type?: AuthType
}

const AuthFlowContainer = () => {
    const type = useAppSelector((s) => s.userState.onboardingType);
    const initializing = useAppSelector((s) => s.userState.initializing);
    const user = useAppSelector((s) => s.userState.currentUser);
    const onboarding = useAppSelector((s) => s.userState.onboarding);
    const twoFactor = useAppSelector((s) => s.userState.twoFactorOpen);
    const twoFactorResolver = useAppSelector((s) => s.userState.twoFactorResolver);
    const fbUser = useAppSelector((s) => s.userState.fbUser);
    const [email, setEmail] = useState(user?.email ?? fbUser?.email ?? '');
    const dispatch = useAppDispatch();

    if (initializing) {
        return <StyledLoadingDiv>
            <Bars
                height="30"
                width="30"
                color={AppColors.pink.dark}
                ariaLabel="bars-loading"
                visible={true}
            />
        </StyledLoadingDiv>
    }

    const onEmailVerified = () => {
        dispatch(setOnboarding({ open: true, type: AuthType.login }));
    }
    const userType = user?.type ?? UserTypeManager.getUserType();
    if (twoFactor && twoFactorResolver !== undefined) {
        return <Enable2FAScreen fromOnboarding={onboarding} />;
    } else if (fbUser !== null && !fbUser.emailVerified) {
        return <VerifyEmailScreen onEmailVerified={onEmailVerified} />;
    } else if (twoFactor) {
        return <Enable2FAScreen fromOnboarding={onboarding} />;
    } else if (process.env.REACT_APP_NEEDS_CODE && !!user && !(userType)) {
        return <SignUpInfoScreen
            initialEmail={email}
            switchToSignUpPressed={() => {
                dispatch(clearAuthError());
                dispatch(setOnboarding({ open: true, type: AuthType.signUp }));
            }}
        />
    } else if (type === AuthType.uploadResume && !!user && !user.onboarded && !(user?.resume)) {
        return <OnboardingResumeScreen replacing={false} onDone={() => dispatch(setOnboarding({ open: true, type: AuthType.selectSkills }))} />;
    } else if (type === AuthType.selectSkills) {
        return <SkillsScreen onboarding hideCloseButton onNext={() => dispatch(setOnboarding({ open: true, type: AuthType.selectPositions }))} onClose={() => dispatch(setOnboarding({ open: true, type: AuthType.none }))} />;
    } else if (type === AuthType.selectPositions) {
        return <TitlesScreen onboarding hideCloseButton onNext={() => dispatch(setOnboarding({ open: true, type: AuthType.jobInfo }))} onClose={() => dispatch(setOnboarding({ open: true, type: AuthType.selectSkills }))} />;
    } else if (type === AuthType.jobInfo) {
        return <JobInfoModal
            onboarding
            initialInfo={user?.info ?? {}}
            onDonePressed={() => dispatch(setOnboarding({ open: false }))}
            onBackPressed={() => dispatch(setOnboarding({ open: true, type: AuthType.selectPositions }))}
        />
    } else if (type === AuthType.googleInfo) {
        return <SignUpInfoScreen
            initialEmail={email}
            switchToSignUpPressed={() => {
                dispatch(clearAuthError());
                dispatch(setOnboarding({ open: true, type: AuthType.signUp }));
            }}
        />
    } else if (!!user && (onboarding || !user.onboarded) && (fbUser === null || fbUser?.emailVerified)) {
        return <OnboardingResumeScreen replacing={type === AuthType.replaceResume} onDone={() => type === AuthType.replaceResume ? dispatch(setOnboarding({ open: false })) : dispatch(setOnboarding({ open: true, type: AuthType.selectSkills }))} />;
    } else if (type === AuthType.signUpInfo) {
        return <SignUpInfoScreen
            initialEmail={email}
            switchToSignUpPressed={() => {
                dispatch(clearAuthError());
                dispatch(setOnboarding({ open: true, type: AuthType.signUp }));
            }}
        />
    } else if (type === AuthType.login) {
        return <LoginScreen switchToSignUpPressed={() => {
            dispatch(clearAuthError());
            dispatch(setOnboarding({ open: true, type: AuthType.signUp }));
        }}
            switchToForgotPasswordPressed={() => {
                dispatch(clearAuthError());
                dispatch(setOnboarding({ open: true, type: AuthType.forgot }));
            }}
        />
    } else if (type === AuthType.forgot) {
        return <ForgotPasswordScreen switchToLoginPressed={() => {
            dispatch(clearAuthError());
            dispatch(setOnboarding({ open: true, type: AuthType.login }));
        }} />
    }
    return <SignUpScreen
        switchToSignUpInfoPressed={(enteredEmail) => {
            setEmail(enteredEmail)
            dispatch(clearAuthError());
            dispatch(setOnboarding({ open: true, type: AuthType.signUpInfo }));
        }}
        switchToLoginPressed={() => {
            dispatch(clearAuthError());
            dispatch(setOnboarding({ open: true, type: AuthType.login }));
        }} />
}

export default AuthFlowContainer;